import { useState } from "react";
import { CheckCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { userApi } from "@/lib/api/admin-api";
import { useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ListSectionHeader } from "@/components/common/ListSectionHeader";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { ApiDatatable } from "@/components/data-table";
import { CreateUserDialog } from "./CreateUserDialog";
import { getUserColumns } from "./columns";
import { UserCard } from "./UserCard";
import type { User } from "@/types/user";

/** Search/pagination state shared by the org-scoped and app-wide users routes. */
export interface UsersListingSearch {
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  status: "all" | "active" | "inactive";
}

export interface UserListingProps {
  /** Current route search params (validated by the host route). */
  searchParams: UsersListingSearch;
  /** Push a search-param update onto the host route's URL. */
  onNavigate: (updater: (prev: UsersListingSearch) => UsersListingSearch) => void;
  /** Open a user's detail page. */
  onRowClick: (user: User) => void;
  /**
   * When true, request users across every organization (super-admin internal
   * view) via the X-Admin-Scope header instead of the caller's own org.
   */
  adminScope?: boolean;
  /**
   * Render the inline "Users" section header (title + Create). Hosts that
   * provide their own page header pass
   * false to avoid a duplicate title and Create button. Defaults to true.
   */
  showSectionHeader?: boolean;
}

export function UserListing({
  searchParams,
  onNavigate,
  onRowClick,
  adminScope,
  showSectionHeader = true,
}: UserListingProps) {
  const { t, i18n } = useLingui();
  const { isAuthenticated, user, hasRole } = useAuth();
  const queryClient = useQueryClient();
  const [total, setTotal] = useState<number>();
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);

  const isSuperAdmin = hasRole("SUPER_ADMIN");
  const isSupport = hasRole("SUPPORT");
  const isOrgAdmin = hasRole("ORG_ADMIN");
  const canActorImpersonate = isSuperAdmin || isSupport || isOrgAdmin;
  const canManageUsers = isSuperAdmin || isSupport || isOrgAdmin;
  const actorOrganizationId = user?.organization?.id;

  const canManageTarget = (target: User): boolean => {
    if (!canManageUsers) return false;
    if (String(target.id) === String(user?.id)) return false;

    const targetOrganizationId =
      target.organization?.id ??
      (typeof target.organizationId === "number"
        ? target.organizationId
        : Number(target.organizationId));

    if (isOrgAdmin && !isSuperAdmin && !isSupport) {
      if (
        !actorOrganizationId ||
        !targetOrganizationId ||
        targetOrganizationId !== actorOrganizationId
      ) {
        return false;
      }
    }

    return true;
  };

  const toggleActiveMutation = useMutation({
    mutationFn: (target: User) =>
      userApi.update(String(target.id), { isActive: !target.isActive }),
    onSuccess: (_result, target) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["users", "stats"] });
      toast.success(
        target.isActive ? t`User set as inactive` : t`User set as active`,
      );
    },
    onError: () => toast.error(t`Failed to update user status`),
  });

  const deleteUserMutation = useMutation({
    mutationFn: (target: User) => userApi.delete(String(target.id)),
    onSuccess: () => {
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["users", "stats"] });
      toast.success(t`User removed`);
    },
    onError: () => toast.error(t`Failed to remove user`),
  });

  const handleDeleteUser = (target: User) => {
    setDeleteTarget(target);
  };

  const deleteTargetName =
    deleteTarget
      ? `${deleteTarget.firstName ?? ""} ${deleteTarget.lastName ?? ""}`.trim() || deleteTarget.email
      : "";

  const canImpersonateTarget = (target: User): boolean => {
    if (!canActorImpersonate || !target.isActive) {
      return false;
    }

    const targetRoleCodes = new Set(
      target.userRoles?.map((userRole) => userRole.role.code) ?? [],
    );

    const hasSupportRole = targetRoleCodes.has("SUPPORT");
    const isPureSuperAdminTarget =
      targetRoleCodes.has("SUPER_ADMIN") && targetRoleCodes.size === 1;

    if (hasSupportRole || isPureSuperAdminTarget) {
      return false;
    }

    if (isOrgAdmin && !isSuperAdmin && !isSupport) {
      const targetOrganizationId =
        target.organization?.id ??
        (typeof target.organizationId === "number"
          ? target.organizationId
          : Number(target.organizationId));

      if (
        !actorOrganizationId ||
        !targetOrganizationId ||
        targetOrganizationId !== actorOrganizationId
      ) {
        return false;
      }
    }

    return true;
  };

  const openImpersonationWindow = (target: User) => {
    const url = new URL("/impersonate", window.location.origin);
    url.searchParams.set("loggedin_as", target.email);
    if (target.organization?.name) {
      url.searchParams.set("organization", target.organization.name);
    }
    if (target.company?.name) {
      url.searchParams.set("concept", target.company.name);
    }
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  const columns = getUserColumns(i18n, {
    canImpersonateTarget,
    onLoginAs: openImpersonationWindow,
    canManageTarget,
    onToggleActive: (target) => toggleActiveMutation.mutate(target),
    onDelete: handleDeleteUser,
  });

  const serverSortableFields = new Set([
    "name",
    "email",
    "createdAt",
    "updatedAt",
    "isActive",
    "organization",
    "company",
    "role",
  ]);

  return (
    <div className="flex flex-col gap-3">
      {showSectionHeader && (
        <ListSectionHeader title={t`Users`} count={total}>
          <Button
            variant="outline"
            className="h-10 gap-2 rounded-[10px] border-(--shell-border) text-sm font-medium text-(--color-text-primary)"
          >
            <CheckCircle className="size-4" />
            {t`Select`}
          </Button>
          <CreateUserDialog />
        </ListSectionHeader>
      )}
      <ApiDatatable
        searchParams={searchParams}
        onNavigate={onNavigate}
        onTotalChange={setTotal}
        queryKey={["admin", "users", adminScope ? "all-orgs" : "org"]}
        queryFn={(params) =>
          userApi.paginate({
            search: params.search,
            page: params.page,
            limit: params.pageSize,
            sortBy: params.sortBy && serverSortableFields.has(params.sortBy) ? params.sortBy : undefined,
            sortOrder: params.sortOrder,
            isActive:
              params.status === "active"
                ? true
                : params.status === "inactive"
                  ? false
                  : undefined,
            adminScope,
          })
        }
        columns={columns}
        renderTile={(rowUser) => (
          <UserCard
            user={rowUser}
            canImpersonate={canImpersonateTarget(rowUser)}
            canManageActions={canManageTarget(rowUser)}
            onLoginAs={openImpersonationWindow}
          />
        )}
        getRowId={(user) => String(user.id)}
        onRowClick={onRowClick}
        enabled={isAuthenticated}
        searchPlaceholder={t`Search users…`}
        emptyMessage={t`No users found`}
        toolbarExtra={
          <Select
            value={searchParams.status}
            onValueChange={(status) =>
              onNavigate((prev) => ({
                ...prev,
                status: status as "all" | "active" | "inactive",
                page: 1,
              }))
            }
          >
            <SelectTrigger className="w-35">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t`All`}</SelectItem>
              <SelectItem value="active">{t`Active`}</SelectItem>
              <SelectItem value="inactive">{t`Inactive`}</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open && !deleteUserMutation.isPending) {
            setDeleteTarget(null);
          }
        }}
        title={t`Remove user?`}
        description={t`This will permanently delete ${deleteTargetName} from your organization.`}
        bullets={[t`This action cannot be undone`]}
        confirmLabel={t`Remove user`}
        onConfirm={() => {
          if (deleteTarget) {
            deleteUserMutation.mutate(deleteTarget);
          }
        }}
        isPending={deleteUserMutation.isPending}
      />
    </div>
  );
}
